import { WithSnackbarProps } from 'notistack';
import { prop } from 'ramda';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useReducer,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';

import {
  Box,
  Container,
  Fab,
  Paper
} from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { SpeedDialAction } from '@material-ui/lab';

import {
  AutocompleteOption,
  SplashScreen
} from '@checkit/react-components';
import {
  AddIcon,
  CheckpointIcon,
  NoCheckpointIcon
} from '@checkit/react-components/build/components/Icons';

import {
  getCheckpointType,
  isGetCheckpointTypeSuccess
} from '../../checkpointTypes/actions';
import { loaderReducer } from '../../common/delayedLoader';
import {
  withQueryParams,
  WithQueryParamsProps
} from '../../common/WithQueryParams';
import MaterialUiLink from '../../components/MaterialUiLink';
import SpeedDial from '../../components/speedDial/SpeedDial';
import {
  getLocationName,
  isGetLocationNameSuccess
} from '../../locations/actions';
import { useAxiosDispatch } from '../../middleware/axios';
import { createSnackbar } from '../../notifications/actions';
import { setRowsPerPage } from '../../preferences/actions';
import { Preferences } from '../../preferences/state';
import { State } from '../../state';
import {
  assignCheckpointLocation,
  clearRecentlyModified,
  getCheckpointCount,
  getCheckpoints,
  isAssignCheckpointLocationSuccess
} from '../actions';
import { messages as bulkImportMessages } from '../bulk/BulkImportCheckpoints.translations';
import { checkpointSelectionReducer } from '../checkpointSelectionReducer';
import { CheckpointNavBar } from '../components/CheckpointNavBar';
import { CheckpointTable } from '../components/CheckpointTable';
import { messages as newCheckpointMessages } from '../new/NewCheckpoint.translations';
import { Checkpoint, Checkpoints } from '../state';

import { messages } from './CheckpointList.translations';
import CheckpointSearch from './CheckpointSearch';

export type UrlParams = 'locationCode' | 'type' | 'name';
export interface Props extends WithSnackbarProps, WithQueryParamsProps<UrlParams> {
  preferences: Preferences;
  checkpoints: Checkpoints;
}

const drawerWidth = 240;

const useStyles = makeStyles((theme: Theme) => ({
  contentDrawerClosed: {
    [theme.breakpoints.up('sm')]: {
      flexGrow: 1,
      marginRight: 0
    }
  },
  contentDrawerOpen: {
    [theme.breakpoints.up('sm')]: {
      flexGrow: 1,
      marginRight: drawerWidth
    }
  },
  speedDial: {
    '&.MuiSpeedDial-directionUp': {
      bottom: -1 * theme.spacing(5),
      right: theme.spacing(3)
    },
    'position': 'absolute'
  }
}));

export const CheckpointListImpl: React.FC<Props> = (props: Props) => {
  const {preferences, checkpoints, location: routeLocation, urlParams, setUrlParam, history} = props;

  const initialName = typeof urlParams.name === 'string' ? urlParams.name : '';
  const initialTypeCode = typeof urlParams.type === 'string' ? urlParams.type : undefined;
  const initialLocationCode = typeof urlParams.locationCode === 'string' ? urlParams.locationCode : '';

  const classes = useStyles();
  const dispatch = useAxiosDispatch( );
  const {formatMessage} = useIntl();

  const [page, setPage] = useState(0);
  const [name, setName] = useState<string>(initialName);
  const [typeCode, setTypeCode] = useState<string | undefined>(initialTypeCode);
  const [locationCode, setLocationCode] = useState<string | undefined>(initialLocationCode);

  const [initialLocation, setInitialLocation] = useState<AutocompleteOption | undefined>(undefined);
  const [initialType, setInitialType] = useState<AutocompleteOption | undefined>(undefined);

  const rowsPerPage = preferences.rowsPerPage;

  const initialState = { loading: true, displayLoader: (checkpoints.count === null || checkpoints.allCodes === null) };

  const [loadingState, loadingDispatch] = useReducer(loaderReducer, initialState);

  const [selectedState, dispatchSelected] = useReducer(checkpointSelectionReducer, new Map<string, Checkpoint>());

  const selectedCheckpoints = Array.from(selectedState.values());

  const drawerOpen = selectedCheckpoints.length > 0;

  useEffect(() => {
    setLocationCode(initialLocationCode);
    setPage(0);
    if (!initialLocationCode) {
      setInitialLocation(undefined);
    } else {
      dispatch(getLocationName(initialLocationCode)).then(function handleResult(result) {
        if (isGetLocationNameSuccess(result)) {
          const location = prop('data', result.payload);
          if (location) {
            setInitialLocation({code: location.id.entityId, name: location.name});
          }
        }
      });
    }
  }, [initialLocationCode, dispatch, setInitialLocation, setLocationCode]);

  useEffect(() => {
    setTypeCode(initialTypeCode);
    setPage(0);
    if (!initialTypeCode) {
      setInitialType(undefined);
    } else {
      dispatch(getCheckpointType(initialTypeCode))
        .then((result) => {
          if (isGetCheckpointTypeSuccess(result)) {
            const checkpointType = prop('data', result.payload);
            if (checkpointType) {
              setInitialType({code: checkpointType.code, name: checkpointType.name});
            }
          }
        });
    }
  }, [initialTypeCode, dispatch, setTypeCode]);

  useEffect(() => {
    return () => {
      dispatch(clearRecentlyModified());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(getCheckpointCount({ filter: name, type: typeCode, locationCode }));
  }, [dispatch, name, typeCode, locationCode]);

  useEffect(() => {
    if (checkpoints.count !== null && checkpoints.count > 0) {
      if (page >= Math.ceil(checkpoints.count / rowsPerPage)) {
        // We're off the end of the page range, decrease page (which will run the effect again)
        setPage(page - 1);
      } else {
        loadingDispatch('loading');
        loadingDispatch('display');
        dispatch(getCheckpoints({
          filter: name,
          locationCode,
          skip: page * rowsPerPage,
          take: rowsPerPage,
          type: typeCode
        }));
      }
    }
  }, [dispatch, checkpoints.count, page, rowsPerPage, name, typeCode, locationCode]);

  useEffect(() => {
    /* Either we have no checkpoints (count is 0).
     * Or the count and the list of checkpoints has been returned.*/
    if (checkpoints.count === 0
      || (checkpoints.count !== null && checkpoints.allCodes !== null)) {
      loadingDispatch('loaded');
    }
  }, [checkpoints]);

  function handleChangePage(
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) {
    dispatch(clearRecentlyModified());
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
    dispatch(clearRecentlyModified());
    setPage(0);
  }

  function handleAssignCheckpoints(location: string | null) {
    return dispatch(assignCheckpointLocation(location, selectedCheckpoints.map(c => c.code)))
      .then((action) => {
        if (isAssignCheckpointLocationSuccess(action)) {
          const message = location ? 'checkpoint.list.assign.success' : 'checkpoint.list.unassign.success';
          dispatchSelected({ type: 'clear' });
          dispatch(createSnackbar({
            message: formatMessage(messages[message]),
            options: {
              variant: 'success'
            }
          }));

          dispatch(getCheckpoints({
            filter: name,
            locationCode,
            skip: page * rowsPerPage,
            take: rowsPerPage,
            type: typeCode
          }));
        } else {
          const message = location ? 'checkpoint.list.assign.failure' : 'checkpoint.list.unassign.failure';
          dispatch(createSnackbar({
            message: formatMessage(messages[message]),
            options: {
              variant: 'error'
            }
          }));
        }
      });
  }

  const onLocationChange = useCallback((_, newLocation: AutocompleteOption | null) => {
    dispatch(clearRecentlyModified());
    const newLocationCode = newLocation ? newLocation.code : '';
    setLocationCode(newLocationCode);
    setUrlParam('locationCode', newLocationCode);
  }, [dispatch, setLocationCode, setUrlParam]);

  const onTypeChange = useCallback((_, newType: AutocompleteOption | null) => {
    dispatch(clearRecentlyModified());
    const newTypeCode = newType === null ? undefined : newType.code;
    setTypeCode(newTypeCode);
    setUrlParam('type', newTypeCode || '');
  }, [setTypeCode, dispatch, setUrlParam]);

  const onNameChange = useCallback((newName: string | null) => {
    if (newName) {
      dispatch(clearRecentlyModified());
    }
    const newNameValue = newName || '';
    setName(newNameValue);
    setUrlParam('name', newNameValue);
  }, [dispatch, setName, setUrlParam]);

  const onAddClick = useCallback(() => {
    history.push({pathname: '/checkpoints/new', state: {previousLocation: routeLocation}});
  }, [history, routeLocation]);

  const onBulkImportClick = useCallback(() => {
    history.push({pathname: '/checkpoints/bulkImport', state: {previousLocation: routeLocation}});
  }, [history, routeLocation]);

  const isEmptyAccount = checkpoints.count === 0
    && !loadingState.displayLoader
    // ensure no filters are applied, otherwise some checkpoints might exist
    // note that locationCode=='' means "no location", not "any location", so _is_ a filter
    && !name && !typeCode && locationCode == null;
  if (isEmptyAccount) {
    return (
      <SplashScreen
        icon={NoCheckpointIcon}
        heading={formatMessage(messages['checkpoints.list.empty.primary'])}
        details={formatMessage(messages['checkpoints.list.empty.secondary'])}
      >
        <Container style={{ textAlign: 'center' }}>
          <Fab
            color="primary"
            variant="extended"
            style={{ marginTop: '1em' }}
            component={MaterialUiLink}
            to={{ pathname: '/checkpoints/new', state: { previousLocation: routeLocation } }}
          >
            <AddIcon style={{ marginRight: '0.3em' }} />
            <span>{formatMessage(messages['checkpoints.list.empty.action'])}</span>
          </Fab>
        </Container>
      </SplashScreen>
    );
  }

  return (
    <>
      <Paper>
        <CheckpointNavBar
          selectedCheckpoints={selectedCheckpoints}
          open={drawerOpen}
          dispatchSelected={dispatchSelected}
          handleAssignCheckpoints={handleAssignCheckpoints}
        />
        <main className={drawerOpen ? classes.contentDrawerOpen : classes.contentDrawerClosed}>
          <Box p={2}>
            <CheckpointSearch
              onLocationChange={onLocationChange}
              onTypeChange={onTypeChange}
              onNameChange={onNameChange}
              initialValues={{location: initialLocation, type: initialType, name: initialName}}
            />
          </Box>
          <CheckpointTable
            selected={Array.from(selectedState.keys())}
            dispatchSelected={dispatchSelected}
            checkpoints={checkpoints}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            loading={loadingState.displayLoader}
          />
        </main>
      </Paper>
      <SpeedDial
        label={formatMessage(messages['checkpoints.list.actions'])}
      >
        <SpeedDialAction
          title={formatMessage(newCheckpointMessages['checkpoint.new.pageTitle'])}
          icon={<AddIcon />}
          onClick={onAddClick}
        />
        <SpeedDialAction
          title={formatMessage(bulkImportMessages['checkpoint.bulkImport.title'])}
          icon={<CheckpointIcon />}
          onClick={onBulkImportClick}
        />
      </SpeedDial>
    </>
  );
};

const mapStateToProps = (state: State) => ({
  checkpoints: state.checkpoints,
  preferences: state.preferences
});
export default connect(mapStateToProps)(withQueryParams<Props, UrlParams>(CheckpointListImpl));
